import type { ChangeEvent, JSX } from 'react';

import { cn } from '../lib/cn';

export interface QuantityStepperProps {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
  /** Accessible name of the item, e.g. "Cantidad de Milanesa con puré". */
  label: string;
  disabled?: boolean;
  className?: string;
}

/**
 * `[-] [input] [+]`. The buttons are `size-11` (44x44px) explicit — `Button`
 * `size="sm"` (36px) doesn't clear the WCAG 2.5.5 touch target, so this
 * component defines its own fixed size instead of reusing it. `max` is
 * purely informative: it never reimplements server-side quotas or cutoffs.
 */
export function QuantityStepper({
  value,
  min = 0,
  max,
  step = 1,
  onChange,
  label,
  disabled = false,
  className,
}: QuantityStepperProps): JSX.Element {
  const atMin = value <= min;
  const atMax = max !== undefined && value >= max;

  function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
    const next = Number(event.target.value);
    if (Number.isNaN(next)) return;
    onChange(next);
  }

  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      <button
        type="button"
        aria-label={`Restar uno a ${label}`}
        disabled={disabled || atMin}
        onClick={() => onChange(value - step)}
        className="flex size-11 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg font-medium text-slate-800 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
      >
        −
      </button>
      <input
        type="number"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={handleInputChange}
        className="h-11 w-16 rounded-lg border border-slate-200 text-center text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      />
      <button
        type="button"
        aria-label={`Sumar uno a ${label}`}
        disabled={disabled || atMax}
        onClick={() => onChange(value + step)}
        className="flex size-11 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg font-medium text-slate-800 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
      >
        +
      </button>
    </div>
  );
}
